import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { StepStatus, TWCAccessibleContent, TWCButton, TWCStep } from '@components';
import { useRecoilState, useRecoilValue } from 'recoil';

import { assets } from '@assets';
import { apiConfig, onboardingTestIDs, Theme } from '@constants';
import { helpers } from '@helpers';
import { useContextNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { onboardingFlowState, screenReaderEnabledState, userSettingsState } from '@store';

import { RadialGradientBackground } from '../../../components/loading/RadialGradientBackground';
import { TWCText } from '../../../components/primitives/TWCText/TWCText';
import { getDspAuthHeader } from '../../../helpers/DspAuth';
import { isAndroid } from '../../../helpers/Platform';
import { accessibleURL } from '../../../helpers/StringUtils';
import { getUserProducts, hasValidProducts } from '../../../hooks/api/useGetUserProducts/useGetUserProducts';

const testIDs = onboardingTestIDs.onboardingFreeTrial;

const OnboardingFreeTrialContentAPI = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'onboardingFreeTrialPage',
  });
  const [{ secondTime }, setFlowState] = useRecoilState(onboardingFlowState);
  const { dspCustomerToken } = useRecoilValue(userSettingsState);
  const [status, setStatus] = useState(StepStatus.INCOMPLETE);
  const [errorMessage, setErrorMessage] = useState('');
  const timeoutReference = useRef(null);
  const signupURL = helpers.getSignupUrl();
  const accountSetupLink = (signupURL ?? apiConfig.WEB_SIGN_UP_URL)?.replace(/http(s)?:\/\//, '');

  useEffect(
    () => () => {
      timeoutReference.current && clearTimeout(timeoutReference.current);
    },
    [],
  );

  const handleActiveAccount = () => {
    timeoutReference.current = setTimeout(() => {
      setStatus(StepStatus.COMPLETE);
      navigationService.replace(onboardingTestIDs.pages.successPage);
      // Show the complete mark for 500ms. which would be enough time for letting user notice success as we will show the Success page for 2 seconds this next
    }, [500]);
  };

  const handleInactiveAccount = () => {
    setStatus(StepStatus.FAILED);
    setErrorMessage(t('noAccountError', { link: accountSetupLink }));
  };

  const handleDonePress = async () => {
    try {
      setFlowState(currentState => ({ ...currentState, signUp: true }));
      if (!secondTime) {
        return navigationService.navigate(onboardingTestIDs.pages.subscriberSignInPage);
      }
      setErrorMessage('');
      const headers = await getDspAuthHeader();
      const { products } = await getUserProducts(headers);

      if (hasValidProducts(products)) {
        if (isAndroid || dspCustomerToken) {
          /**
           * We don't need to activate android devices.
           * If this is an android or already registered then open the success page immediately
           */
          return handleActiveAccount();
        }
        return navigationService.replace(onboardingTestIDs.pages.deviceActivationPage);
      }
      // No account or cancelled
      handleInactiveAccount();
    } catch (error) {
      const statusCode = error.response.status;

      if (statusCode === 424 || statusCode === 404) {
        // 424 means "This user does not have a subscription." (which comes as 404 in the production)
        // Display error message and keep user on this page for next tries
        handleInactiveAccount();
      } else {
        // TODO: Determine how to handle other errors. Just sending to the onboarding error page for now though.
        navigationService.navigate(onboardingTestIDs.pages.errorPage);
      }
    }
  };

  return (
    <OnboardingFreeTrialContentUI
      accountSetupLink={accountSetupLink}
      errorMessage={errorMessage}
      status={status}
      onFinish={handleDonePress}
    />
  );
};

const OnboardingFreeTrialContentUI = ({ accountSetupLink, errorMessage, status, onFinish }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'onboardingFreeTrialPage',
  });

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const step1handle = useContextNodeHandle(testIDs.step1Section);
  const doneButtonHandle = useContextNodeHandle(testIDs.doneButton);
  const noFocus = isAndroid ? doneButtonHandle : null;

  const accessibleLinkText = accessibleURL(accountSetupLink);

  return (
    <RadialGradientBackground>
      <View style={styles.pageContent}>
        <Image source={assets.twcLogo} style={styles.logo} />
        <TWCText
          importantForAccessibility={'no'}
          numberOfLines={1}
          style={styles.pageTitle}
          testID={testIDs.headingText}
          variant={'mediumText60'}
        >
          {t('headingText')}
        </TWCText>

        <View style={styles.steps}>
          <TWCAccessibleContent
            disabledScreenreaderFocus={doneButtonHandle}
            nextFocusDown={doneButtonHandle}
            testID={testIDs.step1Section}
          >
            <TWCStep status={status} step={1}>
              <View style={styles.verticalCenteredRow}>
                <TWCText
                  accessibilityHint={'Press down to continue'}
                  accessibilityLabel={`${t('headingText')} ${t('helperText')} ${accessibleLinkText}`}
                  style={styles.helperText}
                  testID={testIDs.helperText}
                  variant={'regularText44'}
                >
                  {t('helperText')}{' '}
                  <TWCText noTestID style={styles.activateLink} variant={'regularText44'}>
                    {accountSetupLink}
                  </TWCText>
                </TWCText>
              </View>

              {errorMessage ? (
                <TWCText style={styles.errorText} testID={testIDs.errorText} variant={'regularText28'}>
                  {errorMessage}
                </TWCText>
              ) : null}
            </TWCStep>
          </TWCAccessibleContent>
        </View>

        <TWCButton
          accessibilityHint={t('doneButtonAccessibilityHint')}
          accessibilityLabel={t('doneButtonAccessibilityLabel')}
          height={78}
          nextFocusDown={noFocus}
          nextFocusLeft={noFocus}
          nextFocusRight={noFocus}
          nextFocusUp={screenReaderEnabled ? step1handle : noFocus}
          style={styles.doneButton}
          testID={testIDs.doneButton}
          title={t('doneButton')}
          width={263}
          onPress={onFinish}
        />
      </View>
    </RadialGradientBackground>
  );
};

export const OnboardingFreeTrialContent = {
  API: OnboardingFreeTrialContentAPI,
  UI: OnboardingFreeTrialContentUI,
};

const styles = StyleSheet.create({
  activateLink: {
    color: Theme.dodgerLightBlue,
  },
  doneButton: {
    marginTop: Theme.px(240),
  },
  errorText: {
    color: Theme.errorText,
    lineHeight: Theme.px(40),
  },
  helperText: {
    lineHeight: Theme.px(81),
  },
  logo: {
    height: Theme.px(156),
    width: Theme.px(156),
  },
  pageContent: {
    alignItems: 'center',
    paddingVertical: Theme.px(77),
  },
  pageTitle: {
    lineHeight: Theme.px(79),
    marginBottom: Theme.px(41),
    marginTop: Theme.px(47),
  },
  steps: {
    paddingLeft: Theme.px(163),
    width: '100%',
  },
  verticalCenteredRow: {
    alignItems: 'center',
    flexDirection: 'row',
    minHeight: Theme.px(113),
    maxWidth: Theme.px(1600),
  },
});
